import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "@/lib/api";
import { productPath } from "@/lib/seo";

export default function SearchBar({ large, value, onSearch, scopeLabel }) {
  const [q, setQ] = useState(value || "");
  const [sug, setSug] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef();

  // URL'deki sorguyla senkron tut (geri/ileri/yenileme dahil)
  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    if (q.trim().length < 2) { setSug([]); return; }
    const t = setTimeout(() => {
      api.get(`/catalog/suggest?q=${encodeURIComponent(q)}`)
        .then((r) => { setSug(r.data.items); setOpen(true); })
        .catch(() => {});
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const apply = (val) => {
    setOpen(false);
    if (onSearch) onSearch(val.trim());
    else if (val.trim()) navigate(`/urunler?q=${encodeURIComponent(val.trim())}`);
  };

  const handleChange = (v) => {
    setQ(v);
    // kutu tamamen boşaltıldığında uygulanmış aramayı hemen temizle
    if (v.trim() === "" && value) apply("");
  };

  const clearAll = () => { setQ(""); apply(""); };

  return (
    <div ref={ref} className="relative w-full">
      {scopeLabel && (
        <div className="text-xs text-teal-700 font-medium mb-1" data-testid="search-scope-label">
          {scopeLabel} içinde arıyorsunuz
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          data-testid="search-input"
          value={q}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && apply(q)}
          onFocus={() => sug.length && setOpen(true)}
          placeholder="Ürün adı, CAS no, E-kodu, ürün kodu veya marka ile arayın..."
          className={`w-full pl-12 pr-24 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 ${large ? "h-14 text-base" : "h-11 text-sm"}`}
        />
        {q && (
          <button onClick={clearAll} data-testid="search-clear-btn" aria-label="Aramayı temizle"
            className={`absolute top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 ${large ? "right-28" : "right-20"}`}>
            <X className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => apply(q)} data-testid="search-submit-btn"
          className={`absolute right-2 top-1/2 -translate-y-1/2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg px-4 ${large ? "h-10" : "h-8 text-sm"}`}>
          Ara
        </button>
      </div>
      {open && sug.length > 0 && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden"
          data-testid="search-suggestions">
          {sug.map((s) => (
            <button key={s.urun_id} data-testid={`suggest-${s.urun_id}`}
              onClick={() => { setOpen(false); navigate(productPath(s)); }}
              className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b last:border-0 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-900 truncate">{s.urun_adi_tr}</div>
                <div className="text-xs text-slate-500 font-mono" data-nosnippet>Ürün kodu: {s.urun_kodu}</div>
              </div>
              <span className="text-xs text-teal-600 shrink-0">{s.marka}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
